import React, { FC, CSSProperties } from 'react';
// react-bootstrap imports removed (not used in this component)
import { QuestionTypeEnum, StatusEnum } from '../../../shared/models/Enums';
import { LiveAnswer } from '../models/LiveSurvey';
import { ResourcesService } from '../../../shared/services/ResourcesService';
import { useAppSelector } from '../../../app/hooks';
import { selectUserLanguage } from '../../login/loginSlice';
// SurveyLiveAnswersAnswerTitle removed; not needed for numeric NPS rendering

type SurveyLiveAnswersQuestionScaleProps = {
    status: StatusEnum,
    questionType: QuestionTypeEnum,
    questionAnswers: LiveAnswer[],
    questionAnswersSelected: string[],
    onSelect: Function,
};

export const SurveyLiveAnswersQuestionScale: FC<SurveyLiveAnswersQuestionScaleProps> = ({
    status, questionType, questionAnswers, questionAnswersSelected, onSelect
}) => {
    // Render pre-configured answers if provided
    const lang = useAppSelector(selectUserLanguage);
    if (!(status === StatusEnum.Idle && questionType === QuestionTypeEnum.Scale)) return null;

    const sortedAnswers = (questionAnswers || []).slice().sort((a, b) => {
        const ta = a.titleBg ?? '';
        const tb = b.titleBg ?? '';
        const na = Number(ta);
        const nb = Number(tb);
        const bothNumeric = !isNaN(na) && !isNaN(nb);
        if (bothNumeric) return na - nb;
        return String(ta).localeCompare(String(tb));
    });

    // Helper to compute button styles
    const getButtonStyle = (i: number, isSelected: boolean): CSSProperties => {
        // use existing breakpoints: `lg` >= 992px, `sm` <= 575.98px
        const isClient = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
        const isLarge = isClient ? window.matchMedia('(min-width: 992px)').matches : false; // lg and up
        const isSmall = isClient ? window.matchMedia('(max-width: 575.98px)').matches : false; // sm and down

        let bg = '#B6CAAE';
        let color = '#2E2E2E';
        if (i <= 6) { bg = '#F38396CC'; color = '#2E2E2E'; }
        else if (i <= 8) { bg = '#F8D6A9'; color = '#2E2E2E'; }
        else if (i <= 9) { bg = '#B6CAAE'; color = '#2E2E2E'; }
     //   else { bg = '#059669'; color = '#fff'; }

        if (isSelected) {
            bg = '#2E2E2E';
            color = '#fff';
        }

        // sizes tuned for breakpoints: larger on `lg`, compact on `sm`
        const size = isLarge ? 52 : (isSmall ? 24 : 40);
        const fontSize = isLarge ? 18 : (isSmall ? 12 : 16);
        const padding = isLarge ? '8px 12px' : (isSmall ? '4px 6px' : '6px 8px');

        return {
            backgroundColor: bg,
            color,
            border: 'none',
            borderRadius: '8px',
            padding,
            width: size,
            height: size,
            fontWeight: 700,
            fontSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'transform 120ms ease, box-shadow 120ms ease',
            transform: isSelected ? 'scale(1.05)' : 'none',
            boxShadow: isSelected ? '0 1px 4px rgba(0,0,0,0.92)' : '0 1px 4px rgba(0,0,0,0.06)',
        } as CSSProperties;
    };

    return (
        <>
            <div id="rating-section" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11, 1fr)', gap: 8 }}>
                    {sortedAnswers.map(a => {
                        const text = ResourcesService.translateInput(a.titleBg, a.titleEn, a.titleThird, lang)
                        const isSelected = questionAnswersSelected && questionAnswersSelected.includes(a.id)
                        const scaleValue = Number(a.titleEn) || 0; // default to 0 if parsing fails;
                        return (
                            <div key={a.id}>
                                <button
                                    id={`rating-button-${a.id}`}
                                    onClick={() => { onSelect(a); }}
                                    style={getButtonStyle(scaleValue, isSelected)}
                                    aria-pressed={isSelected}
                                    type="button"
                                >
                                   {text}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div id="rating-labels" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <p style={{ color: 'rgba(26,26,26,0.6)', fontSize: 12, fontWeight: 500, maxWidth: 280, margin: 0 }}>
                        {ResourcesService.translate('I would definitely not recommend', lang)}
                    </p>
                    <p style={{ color: 'rgba(26,26,26,0.6)', fontSize: 12, fontWeight: 500, textAlign: 'right', maxWidth: 180, margin: 0 }}>
                        {ResourcesService.translate('More than happy to recommend', lang)}
                    </p>
                </div>
            </div>
        </>
    );
};

export default SurveyLiveAnswersQuestionScale;
